'use client';

import { addHours, addMinutes, format, isToday, setHours, setMinutes, subDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Bell, X } from 'lucide-react';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '../../../components/ui/button';
import { Calendar } from '../../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';

interface ReminderPickerProps {
  dueDate?: Date;
  reminderDate?: Date;
  onReminderSet: (date?: Date) => void;

  // ✅ новое
  showBadge?: boolean; // внутри кнопки показывать badge вместо текста
  triggerVariant?: 'button' | 'chip'; // как рендерить триггер
}

function parseTime(value: string) {
  const [hs, ms] = value.split(':');
  const h = Number(hs);
  const m = Number(ms);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23) return null;
  if (m < 0 || m > 59) return null;
  return { h, m };
}

function timeValueOf(d?: Date) {
  if (!d) return '';
  return format(d, 'HH:mm');
}

function isClose(a: Date, b: Date, toleranceMs = 60_000) {
  return Math.abs(a.getTime() - b.getTime()) <= toleranceMs;
}

function formatReminderLabel(reminderDate?: Date, dueDate?: Date) {
  if (!reminderDate) return 'Напомнить';

  // 1) Если dueDate есть — проверяем относительные пресеты
  if (dueDate) {
    const due = new Date(dueDate);
    const r = new Date(reminderDate);

    const m30 = addMinutes(due, -30);
    const h1 = addHours(due, -1);
    const d1 = subDays(due, 1);

    if (isClose(r, m30)) return 'За 30 минут';
    if (isClose(r, h1)) return 'За 1 час';
    if (isClose(r, d1)) return 'За 1 день';
  }

  // 2) Иначе показываем конкретное время
  const r = new Date(reminderDate);
  const time = format(r, 'HH:mm');

  if (isToday(r)) return `Сегодня, ${time}`;
  // isTomorrow ты можешь импортнуть из date-fns, или упростить:
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (ymd(r) === ymd(tomorrow)) return `Завтра, ${time}`;

  return `${format(r, 'd MMM', { locale: ru })}, ${time}`;
}

function ReminderBadge({
  label,
  onClear,
}: {
  label: string;
  onClear: (e: React.MouseEvent) => void;
}) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-xs text-sky-200"
      title="Напоминание"
    >
      <span className="truncate max-w-[140px]">{label}</span>
      <button
        type="button"
        onClick={onClear}
        className="rounded-full p-0.5 hover:bg-white/10"
        aria-label="Убрать напоминание"
      >
        <X size={12} className="text-sky-200" />
      </button>
    </span>
  );
}

// маленький helper, чтобы сравнить даты без времени
function ymd(d: Date) {
  return format(d, 'yyyy-MM-dd');
}

export const ReminderPicker = memo(function ReminderPicker({
  dueDate,
  reminderDate,
  onReminderSet,
  showBadge = true,
  triggerVariant = 'button',
}: ReminderPickerProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'relative' | 'absolute'>('relative');

  // База для абсолютного режима: если есть reminderDate — он, иначе dueDate, иначе сейчас.
  const base = useMemo(() => reminderDate ?? dueDate ?? new Date(), [reminderDate, dueDate]);

  // Контролируемое значение time input
  const [timeValue, setTimeValue] = useState<string>(timeValueOf(reminderDate ?? dueDate));

  const reminderLabel = useMemo(
    () => formatReminderLabel(reminderDate, dueDate),
    [reminderDate, dueDate],
  );

  useEffect(() => {
    setTimeValue(timeValueOf(reminderDate ?? undefined));
  }, [reminderDate]);

  const close = useCallback(() => setOpen(false), []);

  const setReminder = useCallback(
    (d?: Date) => {
      onReminderSet(d);
    },
    [onReminderSet],
  );

  const setRelative = useCallback(
    (calc: (due: Date) => Date) => {
      if (!dueDate) {
        toast.error('Сначала выберите срок выполнения задачи');
        return;
      }
      const next = calc(new Date(dueDate));
      setReminder(next);
      close();
    },
    [dueDate, setReminder, close],
  );

  const handlePickAbsoluteDay = useCallback(
    (picked?: Date) => {
      if (!picked) {
        setReminder(undefined);
        return;
      }

      const next = new Date(picked);

      // сохраняем время, если оно уже было в reminderDate
      if (reminderDate) {
        next.setHours(reminderDate.getHours(), reminderDate.getMinutes(), 0, 0);
      } else if (dueDate) {
        // иначе можно перенести время из dueDate (удобно)
        next.setHours(dueDate.getHours(), dueDate.getMinutes(), 0, 0);
      } else {
        next.setHours(0, 0, 0, 0);
      }

      setReminder(next);
    },
    [reminderDate, dueDate, setReminder],
  );

  const handleTimeChange = useCallback(
    (value: string) => {
      setTimeValue(value);

      // если время очистили — оставим дату, но сбросим на 00:00
      if (!value) {
        const d = reminderDate ?? dueDate;
        if (!d) return;
        const next = new Date(d);
        next.setHours(0, 0, 0, 0);
        setReminder(next);
        return;
      }

      const parsed = parseTime(value);
      if (!parsed) return;

      const next = setMinutes(setHours(new Date(base), parsed.h), parsed.m);
      setReminder(next);
    },
    [base, dueDate, reminderDate, setReminder],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setReminder(undefined);
      close();
    },
    [setReminder, close],
  );

  const disabled = !dueDate;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              {triggerVariant === 'chip' ? (
                <button
                  type="button"
                  disabled={disabled}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition',
                    !reminderDate
                      ? 'border-slate-600 bg-slate-800/30 text-slate-300 hover:bg-slate-700/40'
                      : 'border-sky-500/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/15',
                    disabled && 'opacity-50 cursor-not-allowed',
                  )}
                  aria-label="Установить напоминание"
                >
                  <Bell size={12} className={cn(reminderDate && 'text-sky-200')} />
                  <span className="truncate max-w-[160px]">
                    {reminderDate ? reminderLabel : 'Напомнить'}
                  </span>

                  {!!reminderDate && (
                    <span
                      role="button"
                      aria-label="Убрать напоминание"
                      className="ml-0.5 inline-flex items-center justify-center rounded-full p-0.5 hover:bg-white/10"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setReminder(undefined);
                      }}
                    >
                      <X size={12} className="text-sky-200" />
                    </span>
                  )}
                </button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(
                    'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600 flex items-center gap-2',
                    !!reminderDate && 'border-sky-500/60 text-slate-100',
                  )}
                  disabled={disabled}
                  aria-label="Установить напоминание"
                >
                  <Bell size={16} className={cn(!!reminderDate && 'text-sky-300')} />

                  {/* ✅ Десктоп: либо badge, либо текст */}
                  <span className="hidden sm:inline">
                    {!reminderDate ? (
                      <span>Напомнить</span>
                    ) : showBadge ? (
                      <ReminderBadge
                        label={reminderLabel}
                        onClear={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setReminder(undefined);
                        }}
                      />
                    ) : (
                      <span className="truncate max-w-[160px]">{reminderLabel}</span>
                    )}
                  </span>

                  {/* ✅ Мобилка */}
                  <span className="sm:hidden">{!reminderDate ? 'Напомнить' : '🔔'}</span>
                </Button>
              )}
            </PopoverTrigger>

            <PopoverContent
              className="w-[320px] p-0 bg-slate-200 border-slate-300"
              align="start"
              side="bottom"
              sideOffset={8}
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-300">
                <div className="text-sm font-semibold text-slate-800">Напоминание</div>

                {!!reminderDate && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-slate-600 hover:text-slate-900"
                    onClick={handleClear}
                    aria-label="Убрать напоминание"
                  >
                    <X size={16} />
                  </Button>
                )}
              </div>

              <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="relative">Перед задачей</TabsTrigger>
                  <TabsTrigger value="absolute">Дата и время</TabsTrigger>
                </TabsList>

                <TabsContent value="relative" className="p-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-start font-normal text-slate-800"
                    onClick={() => setRelative((d) => addMinutes(d, -30))}
                  >
                    За 30 минут
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-start font-normal text-slate-800"
                    onClick={() => setRelative((d) => addHours(d, -1))}
                  >
                    За 1 час
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-start font-normal text-slate-800"
                    onClick={() => setRelative((d) => subDays(d, 1))}
                  >
                    За 1 день
                  </Button>

                  <div className="px-1 pt-2 text-xs text-slate-500">
                    {dueDate ? (
                      <span>Срок: {format(dueDate, 'dd MMM yyyy, HH:mm', { locale: ru })}</span>
                    ) : null}
                  </div>
                </TabsContent>

                <TabsContent value="absolute" className="p-2">
                  <p className="text-sm text-center text-slate-600 mb-2">
                    Выберите точную дату и время
                  </p>

                  <Calendar
                    mode="single"
                    selected={reminderDate}
                    onSelect={(d) => handlePickAbsoluteDay(d || undefined)}
                    initialFocus
                    locale={ru}
                  />

                  <input
                    type="time"
                    className="w-full bg-white p-2 rounded-md mt-2 border border-slate-300 text-slate-800"
                    value={timeValue}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    disabled={!reminderDate && !dueDate}
                  />

                  <div className="pt-2">
                    <Button
                      type="button"
                      className="w-full bg-sky-600 hover:bg-sky-700"
                      onClick={close}
                      disabled={!reminderDate && !dueDate}
                    >
                      Ок
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </PopoverContent>
          </Popover>
        </div>
      </TooltipTrigger>

      <TooltipContent>
        <p>Добавить напоминание</p>
      </TooltipContent>
    </Tooltip>
  );
});
